import type { UserVariables } from "components/shared/AgreementModal";
import type { SalesTitleSelection } from "components/shared/ManagementTitleDetails/SalesDetails/SalesTitleSwitcher";
import { useWorkTitles } from "hooks/useWorkTitles";
import type { SetStateAction } from "react";
import { useMemo } from "react";
import type { Agreement } from "store/agreementsStore";
import type { ICustomField } from "store/customFieldsStore";
import type { TPortalType } from "store/types";
import { EPortalType } from "store/types";

import {
    applySelectedTitleVariables,
    getAgreementWorkTitles,
    getSelectedAgreementTitleId,
    resolveAgreementWorkTitles,
} from "./agreementTitleSource";

export const useAgreementTitleSource = ({
    portalType,
    agreement,
    termWorkId,
    userVariables,
    customFields,
    usedVariableKeys,
    setUserVariables,
    handleVariableChange,
}: {
    portalType: TPortalType;
    agreement: Agreement | null;
    termWorkId?: number | null;
    userVariables: UserVariables | undefined;
    customFields: ICustomField[];
    usedVariableKeys: string[];
    setUserVariables: React.Dispatch<SetStateAction<UserVariables | undefined>>;
    handleVariableChange: (variable?: UserVariables) => void;
}) => {
    const workId = agreement?.workId ?? termWorkId ?? null;
    const storedWorkTitles = useMemo(() => {
        const fromUser = getAgreementWorkTitles(userVariables);
        return fromUser.length ? fromUser : getAgreementWorkTitles(agreement?.userVariables);
    }, [agreement?.userVariables, userVariables]);
    const { titles: fetchedWorkTitles, workName } = useWorkTitles(
        portalType === EPortalType.BOOK && storedWorkTitles.length === 0 ? workId : null,
    );

    const workTitles = useMemo(
        () => resolveAgreementWorkTitles(userVariables, agreement?.userVariables, fetchedWorkTitles, workName),
        [userVariables, agreement?.userVariables, fetchedWorkTitles, workName],
    );
    const selectedTitleId = getSelectedAgreementTitleId(userVariables ?? agreement?.userVariables, workTitles);
    const showTitleSwitcher = portalType === EPortalType.BOOK && workTitles.length > 1;

    const handleSelectTitle = (selection: SalesTitleSelection) => {
        if (typeof selection !== "number" || !userVariables) return;
        const selectedTitle = workTitles.find((title) => Number(title.id) === Number(selection));
        if (!selectedTitle) return;

        const titleCustomFieldUuids = new Set(
            customFields.filter((field) => field.relatedResource === "title").map((field) => field.uuid),
        );
        const nextVariables = applySelectedTitleVariables(
            userVariables,
            selectedTitle,
            workTitles,
            workName,
            titleCustomFieldUuids,
            usedVariableKeys,
        );
        setUserVariables(nextVariables);
        handleVariableChange(nextVariables);
    };

    return { workTitles, selectedTitleId, showTitleSwitcher, handleSelectTitle };
};
